import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FiArrowRight, FiCheck, FiPackage, FiPrinter, FiTag, FiImage,
  FiBookOpen, FiGift, FiHeart, FiMonitor, FiLayers, FiGrid,
} from 'react-icons/fi';
import API from '../utils/api';
import useSiteSettings from '../hooks/useSiteSettings';
import './ServicesPage.css';

/* ── Fallback hero image ─────────────────────────────────────── */
const HERO_FALLBACK =
  'https://images.unsplash.com/photo-1562577309-4932fdd64cd1?w=900&auto=format&fit=crop&q=80';

/* ── Icon map — cycles through these for cards ───────────────── */
const ICONS = [
  { icon: <FiPrinter size={26} />,  color: '#0078BF' },
  { icon: <FiLayers size={26} />,   color: '#16a34a' },
  { icon: <FiPackage size={26} />,  color: '#7c3aed' },
  { icon: <FiImage size={26} />,    color: '#ea580c' },
  { icon: <FiBookOpen size={26} />, color: '#db2777' },
  { icon: <FiGift size={26} />,     color: '#0891b2' },
  { icon: <FiHeart size={26} />,    color: '#f59e0b' },
  { icon: <FiMonitor size={26} />,  color: '#6366f1' },
  { icon: <FiTag size={26} />,      color: '#10b981' },
  { icon: <FiGrid size={26} />,     color: '#ef4444' },
];

/* ── Skeleton card ───────────────────────────────────────────── */
const ServiceSkeleton = () => (
  <div className="svc-card svc-skeleton">
    <div className="svc-sk-icon" />
    <div className="svc-sk-line svc-sk-title" />
    <div className="svc-sk-line" />
    <div className="svc-sk-line svc-sk-short" />
  </div>
);

/* ── Process steps (static) ──────────────────────────────────── */
const STEPS = [
  { num: '01', title: 'Choose Your Product',    desc: 'Browse our wide range of printing products and select what fits your needs.' },
  { num: '02', title: 'Upload Your Design',     desc: 'Upload your artwork or use our free design templates and customization tools.' },
  { num: '03', title: 'We Print & QC',          desc: 'Our team prints your order using premium materials and checks quality at every step.' },
  { num: '04', title: 'Fast Delivery',          desc: 'Your order is packed carefully and delivered to your doorstep within 24–48 hours.' },
];

/* ── Stats bar (static) ──────────────────────────────────────── */
const STATS = [
  { value: '10,000+',   label: 'Happy Customers' },
  { value: '1,50,000+', label: 'Orders Completed' },
  { value: '10+',       label: 'Years of Experience' },
  { value: '24–48 Hrs', label: 'Fast Turnaround' },
];

/* ════════════════════════════════════════════════════════════════
   MAIN PAGE
   ════════════════════════════════════════════════════════════════ */
const ServicesPage = () => {
  const navigate = useNavigate();

  /* hero image from SiteSettings */
  const { data: heroSettings } = useSiteSettings('services_hero');
  const heroImage = heroSettings?.heroImage || HERO_FALLBACK;

  /* services from API */
  const [services, setServices] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get('/public-services');
        if (!cancelled) setServices(data.services || []);
      } catch (err) {
        if (!cancelled) setError('Unable to load services. Please try again later.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <Helmet>
        <title>Our Services — Angel Printers</title>
        <meta name="description" content="Professional printing solutions for every need — business printing, signs, banners, packaging, marketing materials, and more." />
      </Helmet>

      <div className="svc-page">

        {/* ── HERO ──────────────────────────────────────────────── */}
        <section className="svc-hero">
          <div className="container svc-hero-inner">

            {/* Left content (static) */}
            <div className="svc-hero-content">
              <span className="svc-hero-label">OUR SERVICES</span>
              <h1 className="svc-hero-title">
                Professional Printing Solutions for{' '}
                <span className="svc-hero-highlight">Every Need</span>
              </h1>
              <p className="svc-hero-subtitle">
                From business essentials to large format printing, we deliver
                high-quality prints with precision, speed, and care.
              </p>
              <ul className="svc-hero-checks">
                {['Premium Quality Materials', 'Advanced Printing Technology', 'Fast Turnaround & Reliable Delivery'].map((t) => (
                  <li key={t}><FiCheck size={15} className="svc-check-icon" />{t}</li>
                ))}
              </ul>
              <div className="svc-hero-actions">
                <button className="btn btn-primary btn-lg" onClick={() => navigate('/get-quote')}>
                  Get a Quote <FiArrowRight size={16} />
                </button>
                <button className="btn btn-outline btn-lg" onClick={() => navigate('/products')}>
                  Explore Products <FiArrowRight size={16} />
                </button>
              </div>
            </div>

            {/* Right image (admin-controlled) */}
            <div className="svc-hero-image-wrap">
              <img
                src={heroImage}
                alt="Professional printing services"
                className="svc-hero-img"
                loading="eager"
              />
              <div className="svc-hero-badge">
                <span className="svc-hero-badge-icon">🏆</span>
                <div>
                  <div className="svc-hero-badge-title">100% Satisfaction</div>
                  <div className="svc-hero-badge-sub">Quality you can trust</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── SERVICES GRID ─────────────────────────────────────── */}
        <section className="svc-grid-section">
          <div className="container">
            <div className="section-header">
              <span className="label">WHAT WE OFFER</span>
              <h2>Our Printing Services</h2>
              <p>High-quality printing services tailored for businesses, individuals, and organizations of all sizes.</p>
            </div>

            {/* Loading */}
            {loading && (
              <div className="svc-grid">
                {[...Array(6)].map((_, i) => <ServiceSkeleton key={i} />)}
              </div>
            )}

            {/* Error */}
            {!loading && error && (
              <div className="svc-error">
                <p>{error}</p>
                <button className="btn btn-outline" onClick={() => window.location.reload()}>
                  Try Again
                </button>
              </div>
            )}

            {/* Empty */}
            {!loading && !error && services.length === 0 && (
              <div className="svc-empty">
                <FiPrinter size={48} />
                <h3>No services available</h3>
                <p>We're updating our service offerings. Please check back soon.</p>
              </div>
            )}

            {/* Cards */}
            {!loading && !error && services.length > 0 && (
              <div className="svc-grid">
                {services.map((svc, idx) => {
                  const iconData = ICONS[idx % ICONS.length];
                  return (
                    <div key={svc.id} className="svc-card">
                      <div
                        className="svc-card-icon"
                        style={svc.image ? { background: 'transparent', padding: 0, overflow: 'hidden' } : { background: `${iconData.color}18`, color: iconData.color }}
                      >
                        {svc.image
                          ? <img src={svc.image} alt={svc.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} onError={(e) => { e.target.style.display = 'none'; }} />
                          : iconData.icon
                        }
                      </div>
                      <h3 className="svc-card-title">{svc.name}</h3>
                      <p className="svc-card-desc">
                        {svc.short_description || 'Premium printing services delivered with precision and care.'}
                      </p>
                      <Link to={`/services/${svc.slug}`} className="svc-card-link">
                        View Services <FiArrowRight size={14} />
                      </Link>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* ── STATS BAR ─────────────────────────────────────────── */}
        {/* <section className="svc-stats">
          <div className="container svc-stats-inner">
            {STATS.map((s) => (
              <div key={s.label} className="svc-stat">
                <span className="svc-stat-value">{s.value}</span>
                <span className="svc-stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </section> */}

        {/* ── OUR PROCESS (static) ──────────────────────────────── */}
        <section className="svc-process">
          <div className="container">
            <div className="section-header">
              <span className="label">OUR PROCESS</span>
              <h2>Simple Steps to Perfect Prints</h2>
              <p>From your idea to the final print, we make the process easy and hassle-free.</p>
            </div>
            <div className="svc-steps">
              {STEPS.map((s) => (
                <div key={s.num} className="svc-step">
                  <div className="svc-step-num">{s.num}</div>
                  <h4 className="svc-step-title">{s.title}</h4>
                  <p className="svc-step-desc">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA (static) ──────────────────────────────────────── */}
        <section className="svc-cta">
          <div className="container svc-cta-inner">
            <div className="svc-cta-content">
              <h2>Ready to Start Printing?</h2>
              <p>
                Get a custom quote in minutes. Our team is ready to bring your ideas to life
                with premium print quality and fast turnaround.
              </p>
            </div>
            <div className="svc-cta-actions">
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/get-quote')}>
                Get a Free Quote <FiArrowRight size={16} />
              </button>
              <button className="btn btn-lg svc-cta-ghost" onClick={() => navigate('/contact')}>
                Talk to Us
              </button>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default ServicesPage;